import { useState, useCallback } from 'react';
import {
  ReadAudience,
  ReadCompanyRef,
  AnalyzeCsvResponse,
  CsvMapping,
  ImportResponse,
} from '@tinct/core-api-models';

export type ModalStep =
  | 'create'
  | 'add-companies'
  | 'mapping'
  | 'importing'
  | 'success';

export type CompanySourceType = 'csv' | 'companies' | 'crm';

export type UseCreateAudienceModalOptions = {
  initialAudience?: ReadAudience | null;
  initialStep?: ModalStep;
};

type UseCreateAudienceModalReturn = {
  // Step management
  step: ModalStep;
  setStep: (step: ModalStep) => void;

  // Audience
  audience: ReadAudience | null;
  setAudience: (audience: ReadAudience) => void;

  // CSV state
  fileBase64: string | null;
  setFileBase64: (base64: string | null) => void;
  analysisResult: AnalyzeCsvResponse | null;
  setAnalysisResult: (result: AnalyzeCsvResponse | null) => void;
  mapping: CsvMapping;
  setMapping: (mapping: CsvMapping) => void;

  // Import result
  importResult: ImportResponse | null;
  setImportResult: (result: ImportResponse | null) => void;

  // Selected companies (for non-CSV flow)
  selectedCompanies: ReadCompanyRef[];
  setSelectedCompanies: (companies: ReadCompanyRef[]) => void;

  // Navigation helpers
  goToAddCompanies: (audience: ReadAudience) => void;
  goToMapping: (analysisResult: AnalyzeCsvResponse, base64: string) => void;
  goToImporting: () => void;
  goToSuccess: (result: ImportResponse) => void;
  goBack: () => void;
  reset: () => void;
};

export const useCreateAudienceModal = (
  options?: UseCreateAudienceModalOptions
): UseCreateAudienceModalReturn => {
  // Read the option once so the callbacks below read exactly what they list as a
  // dependency. Reaching through `options` in a body that depends only on the
  // field leaves the two out of step, which the compiler refuses.
  const initialAudience = options?.initialAudience;

  const initialStep = initialAudience ? 'add-companies' : (options?.initialStep ?? 'create');
  const [step, setStep] = useState<ModalStep>(initialStep);
  const [audience, setAudience] = useState<ReadAudience | null>(initialAudience ?? null);

  // CSV state
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] =
    useState<AnalyzeCsvResponse | null>(null);
  const [mapping, setMapping] = useState<CsvMapping>({});

  // Import result
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);

  // Selected companies
  const [selectedCompanies, setSelectedCompanies] = useState<ReadCompanyRef[]>(
    []
  );

  const goToAddCompanies = useCallback((newAudience: ReadAudience) => {
    setAudience(newAudience);
    setStep('add-companies');
  }, []);

  const goToMapping = useCallback(
    (result: AnalyzeCsvResponse, base64: string) => {
      setAnalysisResult(result);
      setFileBase64(base64);
      if (result.autoMapping) {
        setMapping(result.autoMapping);
      }
      setStep('mapping');
    },
    []
  );

  const goToImporting = useCallback(() => {
    setStep('importing');
  }, []);

  const goToSuccess = useCallback((result: ImportResponse) => {
    setImportResult(result);
    setStep('success');
  }, []);

  const goBack = useCallback(() => {
    switch (step) {
      case 'add-companies':
        // If we have an initial audience, don't go back to create step
        if (!initialAudience) {
          setStep('create');
        }
        break;
      case 'mapping':
        setStep('add-companies');
        setAnalysisResult(null);
        setFileBase64(null);
        setMapping({});
        break;
      default:
        if (!initialAudience) {
          setStep('create');
        }
    }
  }, [step, initialAudience]);

  const reset = useCallback(() => {
    // Reset to initial state based on options
    if (initialAudience) {
      setStep('add-companies');
      setAudience(initialAudience);
    } else {
      setStep('create');
      setAudience(null);
    }
    setFileBase64(null);
    setAnalysisResult(null);
    setMapping({});
    setImportResult(null);
    setSelectedCompanies([]);
  }, [initialAudience]);

  return {
    step,
    setStep,
    audience,
    setAudience,
    fileBase64,
    setFileBase64,
    analysisResult,
    setAnalysisResult,
    mapping,
    setMapping,
    importResult,
    setImportResult,
    selectedCompanies,
    setSelectedCompanies,
    goToAddCompanies,
    goToMapping,
    goToImporting,
    goToSuccess,
    goBack,
    reset,
  };
};
